import { useEffect, useMemo, useRef, useState } from "react";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import DataObjectIcon from "@mui/icons-material/DataObject";
import ImageIcon from "@mui/icons-material/Image";
import PaletteIcon from "@mui/icons-material/Palette";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../../AppContext";
import { skinApi, type Skin, type SkinUpdate } from "@/api/skin/skinApi";
import { HttpError } from "@/api/common";
import ColorField from "../common/ColorField";
import GraphicUpload from "./GraphicUpload";
import SaveFooter from "../common/SaveFooter";
import Spacer from "../common/Spacer";
import { useToast } from "../common/toast/ToastProvider";
import { useLocalizedStrings } from "../../localization/LocalizedStringsProvider";
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from "../../theme";
import { type ThemeMap } from "./defaultMaterialTheme";
import { useUnsavedChangesPrompt } from "../../hooks/useUnsavedChangesPrompt";

type GraphicKey = "logoLight" | "logoDark" | "loyaltyLogo" | "loyaltyPlaceholder";

const GRAPHIC_SLOTS: {
    key: GraphicKey;
    label: string;
    backdrop: "light" | "dark" | "neutral";
    md5Field: "logoOnLightMd5" | "logoOnDarkMd5" | "loyaltyLogoMd5" | "loyaltyPlaceholderMd5";
    updateField: "logoOnLight" | "logoOnDark" | "loyaltyLogo" | "loyaltyPlaceholder";
    clearField: "clearLogoOnLight" | "clearLogoOnDark" | "clearLoyaltyLogo" | "clearLoyaltyPlaceholder";
}[] = [
    { key: "logoLight", label: "Logo on Light", backdrop: "light", md5Field: "logoOnLightMd5", updateField: "logoOnLight", clearField: "clearLogoOnLight" },
    { key: "logoDark", label: "Logo on Dark", backdrop: "dark", md5Field: "logoOnDarkMd5", updateField: "logoOnDark", clearField: "clearLogoOnDark" },
    { key: "loyaltyLogo", label: "Loyalty Logo", backdrop: "neutral", md5Field: "loyaltyLogoMd5", updateField: "loyaltyLogo", clearField: "clearLoyaltyLogo" },
    { key: "loyaltyPlaceholder", label: "Loyalty Item Placeholder", backdrop: "neutral", md5Field: "loyaltyPlaceholderMd5", updateField: "loyaltyPlaceholder", clearField: "clearLoyaltyPlaceholder" },
];

const emptyGraphicFiles: Record<GraphicKey, File | null> = { logoLight: null, logoDark: null, loyaltyLogo: null, loyaltyPlaceholder: null };
const emptyGraphicMd5s: Record<GraphicKey, string | null> = { logoLight: null, logoDark: null, loyaltyLogo: null, loyaltyPlaceholder: null };
const emptyGraphicClears: Record<GraphicKey, boolean> = { logoLight: false, logoDark: false, loyaltyLogo: false, loyaltyPlaceholder: false };

// Optional public base for stored graphics (e.g. a blob/CDN url). When set, we can preview the current logo;
// otherwise we still show its "Uploaded" status from the MD5.
const GRAPHIC_BASE_URL = import.meta.env.VITE_GRAPHIC_BASE_URL as string | undefined;
const graphicUrl = (md5: string | null): string | undefined =>
    md5 && GRAPHIC_BASE_URL ? `${GRAPHIC_BASE_URL}/${md5}.png` : undefined;

// Parse the stored theme JSON. A blank/invalid theme yields an empty object (no seeded default),
// so the editor reflects exactly what's stored on the server.
const parseTheme = (raw: string | undefined | null): ThemeMap => {
    if (!raw) return {};
    try {
        return JSON.parse(raw) as ThemeMap;
    } catch {
        return {};
    }
};

// Required theme schema (matches the client's exported theme): six contrast sections, each carrying the same
// set of Material color roles, every value an Android ARGB hex (#AARRGGBB).
const REQUIRED_SECTIONS = ["light", "lightMediumContrast", "lightHighContrast", "dark", "darkMediumContrast", "darkHighContrast"] as const;
const REQUIRED_ROLES = [
    "primary", "onPrimary", "primaryContainer", "onPrimaryContainer",
    "secondary", "onSecondary", "secondaryContainer", "onSecondaryContainer",
    "tertiary", "onTertiary", "tertiaryContainer", "onTertiaryContainer",
    "error", "onError", "errorContainer", "onErrorContainer",
    "background", "onBackground", "surface", "onSurface",
    "surfaceVariant", "onSurfaceVariant", "outline", "outlineVariant", "scrim",
    "inverseSurface", "inverseOnSurface", "inversePrimary",
    "surfaceDim", "surfaceBright",
    "surfaceContainerLowest", "surfaceContainerLow", "surfaceContainer", "surfaceContainerHigh", "surfaceContainerHighest",
] as const;
const ARGB_HEX = /^#[0-9a-fA-F]{8}$/;

// Validate a theme against the required schema. Returns a flat list of human-readable problems (empty = valid):
// missing/unexpected sections, missing/unexpected roles, and any value that isn't an #AARRGGBB color.
const validateThemeSchema = (theme: ThemeMap): string[] => {
    const errors: string[] = [];
    if (!theme || typeof theme !== "object" || Array.isArray(theme)) return ["Theme must be a JSON object."];

    const sections = Object.keys(theme);
    for (const s of REQUIRED_SECTIONS) if (!sections.includes(s)) errors.push(`Missing section "${s}".`);
    for (const s of sections) if (!(REQUIRED_SECTIONS as readonly string[]).includes(s)) errors.push(`Unexpected section "${s}".`);

    for (const s of REQUIRED_SECTIONS) {
        const sec = theme[s];
        if (sec == null) continue; // already reported as a missing section
        if (typeof sec !== "object" || Array.isArray(sec)) { errors.push(`Section "${s}" must be an object.`); continue; }

        const roles = Object.keys(sec);
        for (const r of REQUIRED_ROLES) {
            if (!roles.includes(r)) errors.push(`"${s}" is missing "${r}".`);
            else if (!ARGB_HEX.test(sec[r])) errors.push(`"${s}.${r}" must be an ARGB hex like #FF3A608F (got "${sec[r]}").`);
        }
        for (const r of roles) if (!(REQUIRED_ROLES as readonly string[]).includes(r)) errors.push(`"${s}" has an unexpected key "${r}".`);
    }
    return errors;
};

// A Material Theme Builder export nests the schemes under `schemes` with kebab-case section names, 6-digit
// colors, and extra roles. Map its section names to our camelCase master names.
const MATERIAL_SECTION_MAP: Record<string, string> = {
    "light": "light",
    "light-medium-contrast": "lightMediumContrast",
    "light-high-contrast": "lightHighContrast",
    "dark": "dark",
    "dark-medium-contrast": "darkMediumContrast",
    "dark-high-contrast": "darkHighContrast",
};

// "#RRGGBB" -> "#FFRRGGBB" (prepend an opaque alpha channel). Anything already 8-digit (or not a plain
// 6-digit hex) is returned unchanged so the validator can accept or flag it.
const withAlpha = (v: unknown): string => {
    if (typeof v !== "string") return "";
    const m = /^#([0-9a-fA-F]{6})$/.exec(v.trim());
    return m ? `#FF${m[1]}` : v;
};

// Convert a Material Theme Builder export into our master schema: keep only the required sections/roles,
// rename sections to camelCase, and add the FF alpha. The caller runs validateThemeSchema on the result to
// confirm it came out complete and valid.
const transformMaterialExport = (raw: { schemes?: Record<string, Record<string, unknown>> }): ThemeMap => {
    const schemes = raw.schemes ?? {};
    const out: ThemeMap = {};
    for (const [srcKey, destKey] of Object.entries(MATERIAL_SECTION_MAP)) {
        const src = schemes[srcKey];
        if (!src || typeof src !== "object") continue; // absent -> validator reports the missing section
        const section: Record<string, string> = {};
        for (const role of REQUIRED_ROLES) {
            if (role in src) section[role] = withAlpha(src[role]);
        }
        out[destKey] = section;
    }
    return out;
};

const Skinning = () => {
    const strings = useLocalizedStrings();
    const canUpdate = usePermissions().canUpdate(Perm.adminSkinning);
    const toast = useToast();
    const qc = useQueryClient();

    const app = useAppContext();
    // Scope comes from the global top-bar selector.
    const brandId = app.brandId ?? "";
    const [tabValue, setTabValue] = useState<string>("theme");
    const [view, setView] = useState<"visual" | "json">("visual");

    // Working copy + last-saved snapshot (used to compute "dirty")
    const [theme, setTheme] = useState<ThemeMap>({});
    const [savedTheme, setSavedTheme] = useState<ThemeMap>({});
    const [section, setSection] = useState<string>("");

    // Editable buffer for the JSON view + its parse error (if any)
    const [jsonText, setJsonText] = useState<string>("");
    const [jsonError, setJsonError] = useState<string | null>(null);
    // Schema-validation problems surfaced when the user tries to save a non-conforming theme.
    const [schemaErrors, setSchemaErrors] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Graphics: MD5s currently stored on the server + any newly picked files + any marked for removal
    const [currentGraphicMd5s, setCurrentGraphicMd5s] = useState<Record<GraphicKey, string | null>>(emptyGraphicMd5s);
    const [graphicFiles, setGraphicFiles] = useState<Record<GraphicKey, File | null>>(emptyGraphicFiles);
    const [clearedGraphics, setClearedGraphics] = useState<Record<GraphicKey, boolean>>(emptyGraphicClears);

    const { data: skin, isFetching: isSkinLoading } = useQuery<Skin>({
        queryKey: ["skin", brandId],
        enabled: !!brandId,
        // Treat "no skin yet" (404) as a blank skin so the editor shows the default template.
        queryFn: async () => {
            try {
                return await skinApi.get(brandId);
            } catch (e) {
                if (e instanceof HttpError && e.status === 404) {
                    return { brandId, theme: "", logoOnLightMd5: "", logoOnDarkMd5: "", loyaltyLogoMd5: "", loyaltyPlaceholderMd5: "" };
                }
                throw e;
            }
        },
        staleTime: Infinity,
        retry: false,
    });

    // Load the fetched skin into the editor
    useEffect(() => {
        if (!skin) return;
        const loaded = parseTheme(skin.theme);
        setTheme(loaded);
        setSavedTheme(loaded);
        setSection(Object.keys(loaded)[0] ?? "");
        setCurrentGraphicMd5s({
            logoLight: skin.logoOnLightMd5 || null,
            logoDark: skin.logoOnDarkMd5 || null,
            loyaltyLogo: skin.loyaltyLogoMd5 || null,
            loyaltyPlaceholder: skin.loyaltyPlaceholderMd5 || null,
        });
        setGraphicFiles(emptyGraphicFiles);
        setClearedGraphics(emptyGraphicClears);
        setView("visual");
        setJsonError(null);
        setSchemaErrors([]);
    }, [skin]);

    // Drop pending edits when switching brands (the query above reloads the new brand's skin)
    useEffect(() => {
        setGraphicFiles(emptyGraphicFiles);
        setClearedGraphics(emptyGraphicClears);
    }, [brandId]);

    const { mutateAsync: saveSkin, isPending: isSaving } = useMutation({
        mutationFn: (update: SkinUpdate) => skinApi.update(brandId, update),
        onSuccess: () => handleSaveSuccess(),
        onError: () => toast({ severity: "error", message: "Error saving skin." }),
    });

    const sections = useMemo(() => Object.keys(theme), [theme]);
    const entries = useMemo(() => (section ? Object.entries(theme[section] ?? {}) : []), [theme, section]);
    const themeDirty = useMemo(() => JSON.stringify(theme) !== JSON.stringify(savedTheme), [theme, savedTheme]);
    const graphicsDirty = useMemo(() => Object.values(graphicFiles).some(Boolean) || Object.values(clearedGraphics).some(Boolean), [graphicFiles, clearedGraphics]);
    const isDirty = !!brandId && (themeDirty || graphicsDirty);

    // This screen already knew it was dirty — it drives the Save button with it — but never told anything
    // else, so leaving mid-edit discarded the work without asking. Registers the route guard and, through it,
    // the scope selector's guard as well.
    useUnsavedChangesPrompt(isDirty);

    const handleViewChange = (next: "visual" | "json" | null) => {
        if (!next || next === view) return;
        if (next === "json") {
            setJsonText(JSON.stringify(theme, null, 2));
            setJsonError(null);
        }
        setView(next);
    };

    // A theme edit invalidates any previously-shown schema errors — they're recomputed on the next save.
    const clearSchemaErrors = () => setSchemaErrors((prev) => (prev.length ? [] : prev));

    const handleColorChange = (key: string, value: string) => {
        setTheme((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
        clearSchemaErrors();
    };

    const handleJsonChange = (text: string) => {
        setJsonText(text);
        clearSchemaErrors();
        try {
            const parsed = JSON.parse(text) as ThemeMap;
            setTheme(parsed);
            setJsonError(null);
            const keys = Object.keys(parsed);
            if (keys.length && !keys.includes(section)) setSection(keys[0]);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    };

    const handleGraphicSelect = (key: GraphicKey, file: File) => {
        setGraphicFiles((prev) => ({ ...prev, [key]: file }));
        // Picking a replacement supersedes a pending removal.
        setClearedGraphics((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
    };

    // Mark a stored graphic for removal on save (and drop any pending replacement file for that slot).
    const handleGraphicClear = (key: GraphicKey) => {
        setClearedGraphics((prev) => ({ ...prev, [key]: true }));
        setGraphicFiles((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
    };

    const handleGraphicRestore = (key: GraphicKey) => {
        setClearedGraphics((prev) => ({ ...prev, [key]: false }));
    };

    // Load an imported theme into the editor (dirty, so the user reviews it and clicks Save to persist).
    const applyImportedTheme = (imported: ThemeMap) => {
        setTheme(imported);
        setSection(Object.keys(imported)[0] ?? "");
        setJsonText(JSON.stringify(imported, null, 2));
        setJsonError(null);
        setSchemaErrors([]);
        toast({ severity: "success", message: "Theme imported. Review and Save to apply." });
    };

    // Import a theme JSON file. Accepts either (1) our master schema as-is, or (2) a Material Theme Builder
    // export (converted to the master schema). Anything else is rejected.
    const handleImportFile = async (file: File) => {
        let parsed: unknown;
        try {
            parsed = JSON.parse(await file.text());
        } catch {
            toast({ severity: "error", message: "That file isn't valid JSON." });
            return;
        }

        // 1) Already our master schema -> import as-is.
        if (validateThemeSchema(parsed as ThemeMap).length === 0) {
            applyImportedTheme(parsed as ThemeMap);
            return;
        }

        // 2) Material Theme Builder export (has a `schemes` object) -> trim + camelCase + FF alpha, then validate.
        const maybeExport = parsed as { schemes?: Record<string, Record<string, unknown>> };
        if (maybeExport && typeof maybeExport === "object" && maybeExport.schemes && typeof maybeExport.schemes === "object") {
            const converted = transformMaterialExport(maybeExport);
            const errs = validateThemeSchema(converted);
            if (errs.length === 0) {
                applyImportedTheme(converted);
                return;
            }
            // Looked like an export but didn't convert cleanly — show why.
            setSchemaErrors(errs);
            setTabValue("theme");
            toast({ severity: "error", message: "The theme file couldn't be converted to a valid theme — see the errors above." });
            return;
        }

        // 3) Neither schema.
        toast({ severity: "error", message: "The theme JSON is not valid and cannot be imported." });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!brandId) return;
        if (view === "json" && jsonError) {
            toast({ severity: "error", message: "Fix the JSON before saving." });
            return;
        }

        // Only validate (and send) the theme when it actually changed. Must match the required schema.
        if (themeDirty) {
            const errs = validateThemeSchema(theme);
            if (errs.length) {
                setSchemaErrors(errs);
                setTabValue("theme");
                toast({ severity: "error", message: "Theme doesn't match the required schema — see the errors listed above." });
                return;
            }
            setSchemaErrors([]);
        }

        // Send only what changed; omitted fields are left unchanged on the server.
        const update: SkinUpdate = {};
        if (themeDirty) update.theme = JSON.stringify(theme);
        for (const slot of GRAPHIC_SLOTS) {
            const file = graphicFiles[slot.key];
            if (file) update[slot.updateField] = file;              // replace
            else if (clearedGraphics[slot.key]) update[slot.clearField] = true; // remove
        }

        await saveSkin(update);
    };

    const handleSaveSuccess = () => {
        toast({ severity: "success", message: "Skin saved." });
        setSavedTheme(theme);
        setGraphicFiles(emptyGraphicFiles);
        setClearedGraphics(emptyGraphicClears);
        // Refetch so the current MD5s (and persisted theme) reflect the server
        qc.invalidateQueries({ queryKey: ["skin", brandId] });
    };

    const handleCancel = () => {
        setTheme(savedTheme);
        setJsonText(JSON.stringify(savedTheme, null, 2));
        setJsonError(null);
        const keys = Object.keys(savedTheme);
        if (keys.length && !keys.includes(section)) setSection(keys[0]);
        setGraphicFiles(emptyGraphicFiles);
        setClearedGraphics(emptyGraphicClears);
        setSchemaErrors([]);
    };

    return (
        <Box sx={{ ...fillHeight, overflow: "hidden" }}>
            <form onSubmit={handleSubmit} id="skinning-form" style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                {!brandId ? (
                    <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                            Select a brand in the top bar to edit its skin.
                        </Typography>
                    </Box>
                ) : isSkinLoading ? (
                    <LinearProgress />
                ) : (
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: (t) => t.palette.background.paper }}>
                            <TabList onChange={(_e, v) => setTabValue(v)} aria-label="skinning tabs" sx={{ minHeight: 48, "& .MuiTab-root": { minHeight: 48 } }}>
                                <Tab icon={<ColorLensIcon />} iconPosition="start" label="Theme" value="theme" />
                                <Tab icon={<ImageIcon />} iconPosition="start" label="Graphics" value="graphics" />
                            </TabList>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                            <TabPanel value="theme" keepMounted sx={{ height: "100%", p: 0 }}>
                                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2, minHeight: 0 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        {view === "visual" ? (
                                            <FormControl sx={{ width: 320 }} size="small">
                                                <InputLabel id="theme-section-label">Section</InputLabel>
                                                <Select
                                                    labelId="theme-section-label"
                                                    id="theme-section"
                                                    label="Section"
                                                    value={section}
                                                    onChange={(e) => setSection(e.target.value)}
                                                >
                                                    {sections.map((s) => (
                                                        <MenuItem key={s} value={s}>
                                                            {s}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <Box />
                                        )}

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
                                                Import
                                            </Button>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="application/json,.json"
                                                hidden
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f) void handleImportFile(f);
                                                    e.target.value = ""; // allow re-selecting the same file
                                                }}
                                            />
                                            <ToggleButtonGroup value={view} exclusive size="small" onChange={(_, v) => handleViewChange(v)} aria-label="editor view">
                                                <ToggleButton value="visual" aria-label="visual editor">
                                                    <PaletteIcon />
                                                </ToggleButton>
                                                <ToggleButton value="json" aria-label="json editor">
                                                    <DataObjectIcon />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Stack>
                                    </Stack>

                                    <Spacer y={2} />

                                    {schemaErrors.length > 0 && (
                                        <Alert severity="error" onClose={() => setSchemaErrors([])} sx={{ mb: 2, maxHeight: 180, overflow: "auto" }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Theme doesn't match the required schema ({schemaErrors.length}):
                                            </Typography>
                                            <Box component="ul" sx={{ m: "4px 0 0", pl: 2.5 }}>
                                                {schemaErrors.slice(0, 20).map((e, i) => (
                                                    <li key={i}><Typography variant="caption">{e}</Typography></li>
                                                ))}
                                                {schemaErrors.length > 20 && (
                                                    <li><Typography variant="caption">…and {schemaErrors.length - 20} more.</Typography></li>
                                                )}
                                            </Box>
                                        </Alert>
                                    )}

                                    <Box sx={{ flex: 1, minHeight: 0 }}>
                                        {view === "visual" ? (
                                            <TableContainer component={Paper} variant="outlined" sx={{ height: "100%", backgroundColor: "background.default" }}>
                                                <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { borderColor: "divider" } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, width: "40%" }}>Key</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {entries.map(([key, val]) => (
                                                            <TableRow key={key} hover>
                                                                <TableCell sx={{ fontFamily: "monospace" }}>{key}</TableCell>
                                                                <TableCell>
                                                                    <ColorField value={val} onChange={(v) => handleColorChange(key, v)} />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {entries.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={2}>
                                                                    <Typography variant="body2" color="text.secondary">No keys in this section.</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                                                <Box
                                                    component="textarea"
                                                    value={jsonText}
                                                    onChange={(e) => handleJsonChange(e.target.value)}
                                                    spellCheck={false}
                                                    sx={{
                                                        flex: 1,
                                                        minHeight: 0,
                                                        width: "100%",
                                                        resize: "none",
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        border: "1px solid",
                                                        borderColor: jsonError ? "error.main" : "divider",
                                                        bgcolor: "background.paper",
                                                        color: "text.primary",
                                                        overflow: "auto",
                                                        outline: "none",
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                                                        fontSize: 14,
                                                        lineHeight: 1.5,
                                                        "&:focus": { borderColor: jsonError ? "error.main" : "primary.main" },
                                                    }}
                                                />
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, minHeight: 18 }}>
                                                    {jsonError ?? " "}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </TabPanel>

                            <TabPanel value="graphics" keepMounted sx={{ height: "100%", p: 2, overflow: "auto" }}>
                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                    {GRAPHIC_SLOTS.map(({ key, label, backdrop }) => (
                                        <GraphicUpload
                                            key={key}
                                            label={label}
                                            backdrop={backdrop}
                                            uploaded={!!currentGraphicMd5s[key]}
                                            currentUrl={graphicUrl(currentGraphicMd5s[key])}
                                            file={graphicFiles[key]}
                                            cleared={clearedGraphics[key]}
                                            onSelect={(f) => handleGraphicSelect(key, f)}
                                            onClear={() => handleGraphicClear(key)}
                                            onRestore={() => handleGraphicRestore(key)}
                                        />
                                    ))}
                                </Stack>
                            </TabPanel>
                        </Box>
                    </TabContext>
                )}
            </form>

            <SaveFooter
                readOnly={!canUpdate}
                formId="skinning-form"
                formIsDirty={isDirty}
                saving={isSaving}
                saveButtonTitle={strings.save}
                cancelButtonTitle={strings.cancel}
                onCancel={handleCancel}
                // Already reverted in place rather than leaving, which is the behaviour TTD-161 gives the other
                // editors. Bound here too so it is used for the dirty case explicitly.
                onDiscard={handleCancel}
            />
        </Box>
    );
};

export default Skinning;
